import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MediaQueries } from 'custom_hooks/useWindowSize';
import { Fragment, memo, ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

type StepState = 'current' | 'complete' | 'incomplete';

const WizardContainer = styled.div`
    height: 100%;
    display: grid;
    grid-template-rows: auto 1fr;

    @media ${MediaQueries.mediumAndUp} {
        grid-template-rows: auto;
        grid-template-columns: auto 1fr;
        justify-content: space-between;
        grid-template-areas: 'steps content';
    }

    * {
        box-sizing: border-box;
    }
`;

const WizardSteps = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: start;
    flex-direction: row;
    margin: 2rem 1rem;

    @media ${MediaQueries.mediumAndUp} {
        justify-content: flex-start;
        flex-direction: column;
        padding: 3rem;
        border-right: 1px solid ${tokens.border.default};
        margin: 0;
        grid-area: steps;
    }
`;

const WizardStep = styled.div`
    font-size: 14px;
    display: flex;
    flex-wrap: nowrap;
    align-items: center;

    & + & {
        @media ${MediaQueries.mediumAndUp} {
            margin-top: 1rem;
            margin-right: 0;
        }
    }
`;

type StepSidebarItemProps = Readonly<{
    disabled?: boolean;
    $stepState: StepState;
}>;

const StepButton = styled.button<StepSidebarItemProps>`
    display: block;
    width: 2.25rem;
    height: 2.25rem;
    font-size: 12px;
    border: none;
    border-radius: 100%;

    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

    background: ${({ $stepState: buttonStyle }) =>
        buttonStyle === 'incomplete'
            ? tokens.button.secondaryBackground
            : tokens.text.link};

    color: ${({ $stepState: buttonStyle }) =>
        buttonStyle === 'incomplete' ? tokens.text.primary : tokens.text.onColor};
`;

const StepLabel = styled.label<StepSidebarItemProps>`
    margin-left: 0.75rem;

    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

    color: ${({ $stepState }) =>
        $stepState === 'incomplete' ? tokens.text.secondary : tokens.text.primary};

    display: ${({ $stepState }) => ($stepState === 'current' ? 'block' : 'none')};

    @media ${MediaQueries.mediumAndUp} {
        display: block;
    }
`;

const StepLine = styled.div`
    background: ${tokens.border.default};
    margin: 1.25rem 0.75rem;
    width: 1.5rem;
    height: 1px;

    @media ${MediaQueries.mediumAndUp} {
        margin: 0.75rem 1rem;
        height: 2rem;
        width: 1px;
    }
`;

const Content = styled.div`
    position: relative;

    @media ${MediaQueries.mediumAndUp} {
        grid-area: content;
    }
`;

const getStepState = (stepIndex: number, currentStepIndex: number): StepState => {
    if (stepIndex < currentStepIndex) {
        return 'complete';
    }
    if (stepIndex > currentStepIndex) {
        return 'incomplete';
    }
    return 'current';
};

export type WizardStepConfig<TKey extends string = string> = Readonly<{
    key: TKey;
    title: ReactNode;
    render: () => ReactNode;
}>;

export type WizardProps = Readonly<{
    steps: WizardStepConfig[];
    currentStepIndex: number;
    onStepChange: (newStep: number) => void;
    stepClickDisabled?: boolean | number[];
    /**
     * An element that will replace the current step content. To be used, for
     * example, to render a fallback while content is loading.
     */
    contentOverride?: JSX.Element;
}>;

export const Wizard = memo(WizardImpl);
function WizardImpl({
    steps,
    currentStepIndex,
    onStepChange,
    stepClickDisabled,
    contentOverride,
}: WizardProps) {
    const stepsAndStates = useMemo(
        () =>
            steps.map((step, stepIndex) => ({
                step,
                stepIndex,
                stepState: getStepState(stepIndex, currentStepIndex),
            })),
        [currentStepIndex, steps],
    );

    const isStepClickEnabled = useMemo(() => {
        if (Array.isArray(stepClickDisabled)) {
            const disabledSteps = new Set(stepClickDisabled);
            return (stepNum: number) => !disabledSteps.has(stepNum);
        }
        return () => !stepClickDisabled;
    }, [stepClickDisabled]);

    return (
        <WizardContainer>
            <WizardSteps>
                {stepsAndStates.map(({ step: { key, title }, stepIndex, stepState }) => {
                    const isClickEnabled =
                        isStepClickEnabled(stepIndex) && stepState === 'complete';
                    return (
                        <Fragment key={key}>
                            {stepIndex > 0 && <StepLine />}
                            <WizardStep>
                                {stepState === 'complete' && (
                                    <StepButton
                                        type="button"
                                        id={`step-${stepIndex}-btn`}
                                        $stepState="complete"
                                        onClick={() => onStepChange(stepIndex)}
                                        disabled={!isClickEnabled}
                                    >
                                        <FontAwesomeIcon icon={faCheck} size="sm" />
                                    </StepButton>
                                )}
                                {stepState === 'incomplete' && (
                                    <StepButton
                                        type="button"
                                        $stepState="incomplete"
                                        disabled
                                    >
                                        {stepIndex + 1}
                                    </StepButton>
                                )}
                                {stepState === 'current' && (
                                    <StepButton
                                        type="button"
                                        $stepState="current"
                                        disabled
                                    >
                                        {stepIndex + 1}
                                    </StepButton>
                                )}
                                <StepLabel
                                    htmlFor={`step-${stepIndex}-btn`}
                                    disabled={!isClickEnabled}
                                    $stepState={stepState}
                                >
                                    {title}
                                </StepLabel>
                            </WizardStep>
                        </Fragment>
                    );
                })}
            </WizardSteps>
            {contentOverride === undefined ? (
                <Content key={steps[currentStepIndex]?.key}>
                    {steps[currentStepIndex]?.render()}
                </Content>
            ) : (
                contentOverride
            )}
        </WizardContainer>
    );
}
